import React from 'react';
import { Button, Tooltip } from 'antd';
import {
  BulbOutlined,
  BulbFilled,
  DesktopOutlined,
  MoonOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { useTheme } from './ThemeContext';
import type { ThemePreference } from './ThemeContext';
import './theme-mode-selector.css';

export function Text(props: React.HTMLProps<HTMLSpanElement>) {
  const { style, ...otherProps } = props;
  return (
    <span style={{ ...style }} {...otherProps}>
      {props.children}
    </span>
  );
}

export function View(props: React.HTMLProps<HTMLDivElement>) {
  const { style, ...otherProps } = props;
  return (
    <div style={{ backgroundColor: 'transparent', ...style }} {...otherProps}>
      {props.children}
    </div>
  );
}

export function BackgroundView(props: React.HTMLProps<HTMLDivElement>) {
  const { style, children, ...otherProps } = props;
  return (
    <div style={{ position: 'relative', flex: 1, padding: 12, ...style }} {...otherProps}>
      <img
        src={``}
        alt="background"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
        }}
      />
      {children}
    </div>
  );
}

export function BaseButton({ style, title, color, isLoading, disabled, onClick }: any) {
  return (
    <button
      style={{
        ...style,
        backgroundColor: disabled ? '#ccc' : color || '#007BFF',
        borderRadius: '1002px',
        border: '1px solid',
        padding: '16px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transform: disabled ? 'scale(1)' : 'scale(0.97)',
      }}
      onClick={onClick}
      disabled={disabled}
    >
      {isLoading ? (
        <span style={{ color }}>
          <span className="spinner" />
        </span>
      ) : (
        <span style={{ color }}>{title}</span>
      )}
    </button>
  );
}

export function PrimaryButton(props: any) {
  const { style, disabled, ...otherProps } = props;
  const backgroundColor = disabled ? '#ccc' : '#007BFF';
  const color = disabled ? '#f0f0f0' : '#fff';

  return <BaseButton style={{ backgroundColor, ...style }} color={color} {...otherProps} />;
}

export function CurrencyText(props: { value: number }) {
  return (
    <Text>
      {props.value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })}
    </Text>
  );
}

export function Link(props: React.HTMLProps<HTMLAnchorElement>) {
  const { style, ...otherProps } = props;
  return <a style={{ color: '#007BFF', ...style }} {...otherProps} />;
}

export function HorizontalRule(props: React.HTMLProps<HTMLDivElement>) {
  return <div style={{ height: '1px', backgroundColor: '#ddd', ...props.style }} />;
}

export interface ThemeToggleProps {
  style?: React.CSSProperties;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ style }) => {
  const { theme, toggleTheme } = useTheme();

  return (
    <Tooltip
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      placement="bottom"
      mouseEnterDelay={0.5}
      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
    >
      <Button
        type="text"
        icon={theme === 'dark' ? <BulbOutlined /> : <BulbFilled />}
        onClick={toggleTheme}
        style={{
          fontSize: '20px',
          ...style,
        }}
      />
    </Tooltip>
  );
};

/**
 * ThemeToggleIcon — bulb icon reflecting current theme.
 *
 * `iconStyle` is merged BEFORE the consumer-supplied `style`, so consumers can
 * still override any defaults. The advisor portal calls this without a default
 * size; the client portal supplies `fontSize: "20px"` via `iconStyle` to
 * preserve its previous appearance verbatim.
 */
export const ThemeToggleIcon: React.FC<ThemeToggleProps & { iconStyle?: React.CSSProperties }> = ({
  style,
  iconStyle,
}) => {
  const { theme } = useTheme();
  const mergedStyle = { ...iconStyle, ...style };

  return theme === 'dark' ? (
    <BulbOutlined style={mergedStyle} />
  ) : (
    <BulbFilled style={mergedStyle} />
  );
};

const THEME_OPTIONS: Array<{
  icon: React.ReactNode;
  label: string;
  value: ThemePreference;
}> = [
  { icon: <SunOutlined />, label: 'Light', value: 'light' },
  { icon: <MoonOutlined />, label: 'Dark', value: 'dark' },
  { icon: <DesktopOutlined />, label: 'System', value: 'system' },
];

export interface ThemeModeSelectorProps {
  className?: string;
  onChange: (preference: ThemePreference) => void;
  style?: React.CSSProperties;
  value: ThemePreference;
}

export const ThemeModeSelector: React.FC<ThemeModeSelectorProps> = ({
  className,
  onChange,
  style,
  value,
}) => (
  <div
    aria-label="Theme"
    className={['theme-mode-selector', className].filter(Boolean).join(' ')}
    role="radiogroup"
    style={style}
  >
    {THEME_OPTIONS.map((option) => (
      <Tooltip key={option.value} mouseEnterDelay={0.4} placement="bottom" title={option.label}>
        <button
          aria-checked={value === option.value}
          aria-label={`${option.label} theme`}
          className="theme-mode-selector__option"
          onClick={(event) => {
            event.stopPropagation();
            onChange(option.value);
          }}
          role="radio"
          type="button"
        >
          {option.icon}
        </button>
      </Tooltip>
    ))}
  </div>
);
